import Papa from 'papaparse';
import { safeParseISO } from './financialCalculations.js';
import { differenceInYears, addYears, addDays, format, getDaysInYear } from 'date-fns';

// Path to the new work-life expectancy table
const WORKLIFE_TABLE_PATH = '/data/us/worklifeexpectancy/us_worklife_expectancy_full.csv';

// Column indices for the new CSV structure
const AGE_COL_INDEX = 0;
const SEX_COL_INDEX = 1;
const EDUCATION_COL_INDEX = 2;
const WLE_COL_INDEX = 3; // WorklifeExpectancy is the 4th column (index 3)
const HEADER_ROWS = 1; // Number of header rows to skip

// Mapping from application's education level terms to CSV's EducationGroup terms
// This needs to be comprehensive based on possible values from Step1_CaseInfo.js and CSV
const EDUCATION_APP_TO_CSV_MAP = {
    "Did not complete High School": "Did not complete High School", // Corrected: Direct map based on CSV data
    "High School Diploma or GED": "High School Diploma or GED",
    "Some College, No Degree": "Some College, No Degree", // Corrected: Direct map
    "Associate's Degree": "Associate's Degree", // Assuming CSV has this exact string too, if not, needs grouping
    "Bachelor's Degree": "Bachelor's Degree",
    "Master's Degree": "Master's Degree",
    "Doctoral Degree (PhD, EdD)": "Doctoral Degree (PhD, EdD), Professional Degree (MD, JD, DDS)", // Corrected to exact CSV string
    "Professional Degree (MD, JD, DDS)": "Doctoral Degree (PhD, EdD), Professional Degree (MD, JD, DDS)", // Corrected to exact CSV string
    "Trade/Vocational Certification": "High School Diploma or GED", // Defaulting as per discussion
    "Other": "High School Diploma or GED" // Defaulting as per discussion
};
const DEFAULT_EDUCATION_CSV = "High School Diploma or GED"; // Fallback if mapping fails or input is unexpected

/**
 * Calculates the work-life expectancy (in years) and the corresponding retirement date.
 * Fetches CSV data, parses it, finds the relevant work-life expectancy based on age, gender, and education,
 * and calculates the retirement date.
 *
 * @param {string} claimantDOB - Date of Birth in 'YYYY-MM-DD' format.
 * @param {string} claimantGender - Gender ('Male', 'Female', or other).
 * @param {string} claimantEducation - Education level from the application.
 * @returns {Promise<{worklifeYears: number, retirementDate: string}|null>} An object with work-life years and retirement date, or null if calculation fails.
 */
export const calculateWorklifeExpectancy = async (
    claimantDOB,
    claimantGender,
    claimantEducation,
    claimantCountry,
    claimantEmploymentStatus,
    claimantSector // new: "Public sector", "Private sector", "Not sure"
) => {
    if (!claimantDOB) {
        console.warn("Cannot calculate WLE: Claimant DOB is missing.");
        return null;
    }
    // claimantEducation can be undefined/null if not provided, will use default.

    try {
        const dobDate = safeParseISO(claimantDOB);
        if (isNaN(dobDate.getTime())) {
            console.error("Invalid Date of Birth provided for WLE:", claimantDOB);
            return null;
        }

        const currentDate = new Date();
        const age = differenceInYears(currentDate, dobDate);
        if (age < 0) {
            console.error("Date of Birth is in the future for WLE:", claimantDOB);
            return null;
        }

        // CANADIAN LOGIC BRANCH
        if (claimantCountry === "Canada") {
            // Canadian CSV: /data/canada/worklifeexpectancy/canada_retirement_age_2024_by_worker_class.csv
            // Columns: Sex,Class of worker,RetirementAge
            // Use claimantGender, claimantEmploymentStatus, claimantSector
            // Calculate worklifeYears = RetirementAge - age (if age < RetirementAge, else 0)
            const CANADA_WLE_CSV_PATH = '/data/canada/worklifeexpectancy/canada_retirement_age_2024_by_worker_class.csv';

            // Map gender to CSV values
            let targetSex = "Both";
            if (claimantGender === "Male") targetSex = "Male";
            else if (claimantGender === "Female") targetSex = "Female";
            // else "Both" for non-binary, prefer not to say, other

            // Map employment status and sector to CSV "Class of worker"
            let targetClass = "Total, all retirees";
            if (claimantEmploymentStatus === "Self-Employed") {
                targetClass = "Self-employed";
            } else if (
                claimantEmploymentStatus === "Employed Full-Time" ||
                claimantEmploymentStatus === "Employed Part-Time"
            ) {
                if (claimantSector === "Public sector") {
                    targetClass = "Public sector employees";
                } else if (claimantSector === "Private sector") {
                    targetClass = "Private sector employees";
                } else {
                    targetClass = "Total, all retirees";
                }
            } // else leave as "Total, all retirees"

            try {
                const response = await fetch(CANADA_WLE_CSV_PATH);
                if (!response.ok) {
                    throw new Error(`Failed to fetch Canadian work-life expectancy table: ${response.statusText} (path: ${CANADA_WLE_CSV_PATH})`);
                }
                const rawCsvText = await response.text();

                return new Promise((resolve) => {
                    Papa.parse(rawCsvText, {
                        delimiter: ',',
                        header: true,
                        skipEmptyLines: true,
                        complete: (results) => {
                            if (results.errors.length > 0) {
                                results.errors.forEach(err => console.error(`Papaparse CAN WLE Error: ${err.message} (Code: ${err.code}, Row: ${err.row})`));
                                resolve(null); return;
                            }
                            const dataRows = results.data;
                            // Find the row matching targetSex and targetClass
                            let foundRow = dataRows.find(row =>
                                row["Sex"] === targetSex && row["Class of worker"] === targetClass
                            );
                            // Fallback: if not found, try "Both" for sex
                            if (!foundRow && targetSex !== "Both") {
                                foundRow = dataRows.find(row =>
                                    row["Sex"] === "Both" && row["Class of worker"] === targetClass
                                );
                            }
                            // Fallback: if still not found, use "Total, all retirees" and "Both"
                            if (!foundRow) {
                                foundRow = dataRows.find(row =>
                                    row["Sex"] === "Both" && row["Class of worker"] === "Total, all retirees"
                                );
                            }
                            if (!foundRow) {
                                console.error("No matching row found in Canadian WLE CSV for:", targetSex, targetClass);
                                resolve(null); return;
                            }
                            const retirementAge = parseFloat(foundRow["RetirementAge"]);
                            if (isNaN(retirementAge)) {
                                console.error("Invalid RetirementAge in Canadian WLE CSV row:", foundRow);
                                resolve(null); return;
                            }
                            let worklifeYears = retirementAge - age;
                            if (worklifeYears < 0) worklifeYears = 0;

                            // Calculate retirement date based on retirementAgeFromCSV and DOB (not current date)
                            const dobDateForRetirementCalc = safeParseISO(claimantDOB);
                            let newRetirementDateObj = addYears(dobDateForRetirementCalc, Math.floor(retirementAge));
                            const fractionalAgeForRetirement = retirementAge - Math.floor(retirementAge);
                            const daysInYearAtRetirementAge = getDaysInYear(addYears(dobDateForRetirementCalc, Math.floor(retirementAge)));
                            const additionalDaysForRetirement = Math.round(fractionalAgeForRetirement * daysInYearAtRetirementAge);
                            newRetirementDateObj = addDays(newRetirementDateObj, additionalDaysForRetirement);
                            const finalFormattedRetirementDate = format(newRetirementDateObj, 'yyyy-MM-dd');
                            resolve({ worklifeYears, retirementDate: finalFormattedRetirementDate });
                        },
                        error: (error) => {
                            console.error("Papaparse CAN WLE encountered an error:", error);
                            resolve(null);
                        }
                    });
                });
            } catch (error) {
                console.error("Error in Canadian WLE calculation:", error);
                return null;
            }
        }

        // US LOGIC (existing)
        // Map application education level to CSV education group
        let educationCsvGroup = EDUCATION_APP_TO_CSV_MAP[claimantEducation] || DEFAULT_EDUCATION_CSV;
        if (!EDUCATION_APP_TO_CSV_MAP[claimantEducation] && claimantEducation) { // Log if input was given but not mapped
            console.warn(`WLE: Education level "${claimantEducation}" not directly mapped. Using default CSV group "${DEFAULT_EDUCATION_CSV}".`);
        } else if (!claimantEducation) {
            console.warn(`WLE: Claimant Education is missing. Using default CSV group "${DEFAULT_EDUCATION_CSV}".`);
        }
    
        const response = await fetch(WORKLIFE_TABLE_PATH);
        if (!response.ok) {
            const attemptedUrl = new URL(WORKLIFE_TABLE_PATH, window.location.origin);
            throw new Error(`Failed to fetch work-life expectancy table: ${response.statusText} (path: ${WORKLIFE_TABLE_PATH}, attempted URL: ${attemptedUrl})`);
        }
        const rawCsvText = await response.text();
    
        return new Promise((resolve) => {
            Papa.parse(rawCsvText, {
                delimiter: ',',
                header: false,
                skipEmptyLines: true,
                complete: (results) => {
                    let worklifeYears = null;
                    if (results.errors.length > 0) {
                        results.errors.forEach(err => console.error(`Papaparse WLE Error: ${err.message} (Code: ${err.code}, Row: ${err.row})`));
                        resolve(null); return;
                    }
    
                    const dataRows = results.data.slice(HEADER_ROWS);
                    if (dataRows.length === 0) {
                        console.error(`No actual data rows found after skipping header in ${WORKLIFE_TABLE_PATH}.`);
                        resolve(null); return;
                    }
    
                    // Function to find WLE based on current filters
                    const findWLE = (targetSexCsv, targetEducationCsv) => {
                        let firstAgeInData = Infinity, lastAgeInData = -Infinity;
                        let firstRowWLE = null; // WLE for the first age found for this filter
                        // let lastRowWLE = null; // Not strictly needed as WLE is 0 if age > lastAgeInData
    
                        for (const row of dataRows) {
                            if (!Array.isArray(row) || row.length <= WLE_COL_INDEX) continue;
    
                            const rowAge = parseInt(row[AGE_COL_INDEX], 10);
                            const rowSex = String(row[SEX_COL_INDEX]).trim();
                            const rowEducation = String(row[EDUCATION_COL_INDEX]).trim();
    
                            if (isNaN(rowAge)) continue;
    
                            // Track min/max age for the current filter criteria (sex, education)
                            if (rowSex === targetSexCsv && rowEducation === targetEducationCsv) {
                                if (rowAge < firstAgeInData) {
                                    firstAgeInData = rowAge;
                                    const wleVal = parseFloat(row[WLE_COL_INDEX]);
                                    if(!isNaN(wleVal)) firstRowWLE = wleVal;
                                }
                                lastAgeInData = Math.max(lastAgeInData, rowAge);
                            }
    
                            if (rowAge === age && rowSex === targetSexCsv && rowEducation === targetEducationCsv) {
                                const wleStr = row[WLE_COL_INDEX];
                                const parsedWLE = parseFloat(wleStr);
                                if (!isNaN(parsedWLE)) {
                                    return parsedWLE;
                                } else {
                                    console.warn(`WLE: Failed to parse WLE value "${wleStr}" for matching row:`, row);
                                    return null; // Parsing failed for the matched row
                                }
                            }
                        }
                        // Handle age edge cases if no exact age match for the current filter
                        if (firstAgeInData === Infinity) { // No data matched this filter at all
                             console.warn(`WLE: No data found for Sex="${targetSexCsv}", Educ="${targetEducationCsv}".`);
                             return null;
                        }
                        if (age < firstAgeInData) {
                            //console.log(`WLE: Age ${age} is less than first age in data (${firstAgeInData}) for Sex=${targetSexCsv}, Educ=${targetEducationCsv}. Using WLE: ${firstRowWLE}`);
                            return firstRowWLE;
                        }
                        if (age > lastAgeInData) {
                            //console.log(`WLE: Age ${age} is greater than last age in data (${lastAgeInData}) for Sex=${targetSexCsv}, Educ=${targetEducationCsv}. Setting WLE to 0.`);
                            return 0;
                        }
                        console.warn(`WLE: No exact age match for Age=${age}, Sex=${targetSexCsv}, Educ=${targetEducationCsv} within table range [${firstAgeInData}-${lastAgeInData}].`);
                        return null; // No exact match within the filtered data's age range
                    };
    
                    // Attempt 1: Exact claimant gender and mapped/default education
                    // Map claimantGender to CSV values: "Male", "Female", "Both"
                    let targetSexForCsv = claimantGender === 'Male' ? 'Male' : (claimantGender === 'Female' ? 'Female' : 'Both');
                    if (claimantGender !== 'Male' && claimantGender !== 'Female') {
                        console.warn(`WLE: Claimant gender "${claimantGender}" is not Male/Female. Defaulting to "Both" for CSV lookup.`);
                    }
                    worklifeYears = findWLE(targetSexForCsv, educationCsvGroup);
    
                    // Attempt 2: Fallback to "Both" sex if specific gender yielded no result (and wasn't "Both" already due to non-Male/Female input)
                    if (worklifeYears === null && targetSexForCsv !== 'Both') {
                        console.warn(`WLE: No match for Sex="${targetSexForCsv}", Educ="${educationCsvGroup}". Falling back to Sex="Both".`);
                        worklifeYears = findWLE('Both', educationCsvGroup);
                    }
                    
                    // Attempt 3: If education was defaulted (i.e., claimantEducation was provided but not in map) and still no match, log this.
                    if (worklifeYears === null && claimantEducation && !EDUCATION_APP_TO_CSV_MAP[claimantEducation]) {
                         console.warn(`WLE: No match even with defaulted Education="${educationCsvGroup}" (original app input: "${claimantEducation}") and Sex="${targetSexForCsv}". This might indicate a data gap for the age group.`);
                    } else if (worklifeYears === null) {
                        // This means no match was found even with primary education group and sex (plus sex fallback)
                        console.warn(`WLE: No match found for Age=${age}, MappedEduc="${educationCsvGroup}", Sex="${targetSexForCsv}".`);
                    }
    
    
                    if (worklifeYears === null || isNaN(worklifeYears)) {
                        console.error(`WLE: Failed to determine valid WLE for Age=${age}, Gender(App)="${claimantGender}", Educ(App)="${claimantEducation}".`);
                        resolve(null); return;
                    }
    
                    const wholeYears = Math.floor(worklifeYears);
                    const fractionalYear = worklifeYears - wholeYears;
                    // Use getDaysInYear for accuracy based on the *target* year after adding wholeYears
                    const targetYearEndDate = addYears(currentDate, wholeYears);
                    const daysInTargetYear = getDaysInYear(targetYearEndDate);
                    const additionalDays = Math.round(fractionalYear * daysInTargetYear);
    
                    let retirementDate = addYears(currentDate, wholeYears);
                    retirementDate = addDays(retirementDate, additionalDays);
    
                    const formattedRetirementDate = format(retirementDate, 'yyyy-MM-dd');
                    //console.log(`Calculated Retirement Date: ${formattedRetirementDate}, Worklife Years: ${worklifeYears}`);
    
                    resolve({ worklifeYears: worklifeYears, retirementDate: formattedRetirementDate });
    
                }, // End of complete callback
                error: (error) => {
                    console.error("Papaparse WLE encountered an error:", error);
                    resolve(null); // Resolve with null on parser error
                }
            }); // End of Papa.parse
        }); // End of Promise wrapper
    
    } catch (error) {
        console.error("Error in calculateWorklifeExpectancy function:", error);
        return null; // Return null on any fetch/setup error
    }
};
